import React, { useState } from "react";
import {
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  Phone,
  UserRound,
  Gift,
  Loader2,
} from "lucide-react";

import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
} from "firebase/auth";

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import {
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  auth,
  db,
  googleProvider,
} from "../firebase/firebase";

function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // -----------------------------------------
  // Referral code from URL
  // Example:
  // /signup?ref=REF-ABC123
  // -----------------------------------------

  const referralFromUrl = searchParams.get("ref") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    referralCode: referralFromUrl,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // -----------------------------------------
  // Input Change
  // -----------------------------------------

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setError("");
  };

  // -----------------------------------------
  // Firebase Error Messages
  // -----------------------------------------

  const getFirebaseErrorMessage = (firebaseError) => {
    switch (firebaseError?.code) {
      case "auth/email-already-in-use":
        return "This email is already registered. Please login instead.";

      case "auth/invalid-email":
        return "Please enter a valid email address.";

      case "auth/weak-password":
        return "Password must be at least 6 characters.";

      case "auth/network-request-failed":
        return "Network error. Please check your internet connection.";

      case "auth/popup-closed-by-user":
        return "Google login was cancelled.";

      case "auth/popup-blocked":
        return "Google popup was blocked. Please allow popups in your browser.";

      case "auth/account-exists-with-different-credential":
        return "An account already exists with this email using another login method.";

      case "auth/operation-not-allowed":
        return "This sign-in method is not enabled in Firebase.";

      case "auth/too-many-requests":
        return "Too many attempts. Please try again later.";

      default:
        return (
          firebaseError?.message ||
          "Something went wrong. Please try again."
        );
    }
  };

  // -----------------------------------------
  // Create Firestore User
  // -----------------------------------------

  const createUserDocument = async ({
    user,
    name = "",
    phone = "",
    referredBy = "",
  }) => {
    const userRef = doc(db, "users", user.uid);

    // Check if user document already exists
    const existingUser = await getDoc(userRef);

    if (existingUser.exists()) {
      return existingUser.data();
    }

    // Generate user's personal referral code
    const ownReferralCode = `REF-${user.uid
      .slice(0, 8)
      .toUpperCase()}`;

    const userData = {
      uid: user.uid,

      name: name.trim() || user.displayName || "",

      email: user.email || "",

      phone: phone.trim(),

      role: "user",

      referralCode: ownReferralCode,

      referredBy: referredBy.trim(),

      paymentStatus: "not_paid",

      softwareActivated: false,

      createdAt: serverTimestamp(),

      updatedAt: serverTimestamp(),
    };

    await setDoc(userRef, userData);

    return userData;
  };

  // -----------------------------------------
  // Email / Password Signup
  // -----------------------------------------

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");

    // Name
    if (!form.name.trim()) {
      setError("Please enter your full name.");
      return;
    }

    // Email
    if (!form.email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    // Phone
    const cleanPhone = form.phone.replace(/\D/g, "");

    if (!cleanPhone) {
      setError("Please enter your phone number.");
      return;
    }

    if (cleanPhone.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    // Password
    if (!form.password) {
      setError("Please create a password.");
      return;
    }

    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    // Confirm Password
    if (!form.confirmPassword) {
      setError("Please re-enter your password.");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      // -------------------------------------
      // 1. Create Firebase Authentication User
      // -------------------------------------

      const userCredential =
        await createUserWithEmailAndPassword(
          auth,
          form.email.trim(),
          form.password
        );

      const user = userCredential.user;

      // -------------------------------------
      // 2. Save Display Name in Firebase Auth
      // -------------------------------------

      await updateProfile(user, {
        displayName: form.name.trim(),
      });

      // -------------------------------------
      // 3. Create Firestore User Document
      // -------------------------------------

      await createUserDocument({
        user,
        name: form.name,
        phone: cleanPhone,
        referredBy: form.referralCode,
      });

      // -------------------------------------
      // 4. Go to Dashboard
      // -------------------------------------

      navigate("/dashboard", {
        replace: true,
      });
    } catch (firebaseError) {
      console.error("Signup Error:", firebaseError);

      setError(
        getFirebaseErrorMessage(firebaseError)
      );
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------------------
  // Google Signup
  // -----------------------------------------

  const handleGoogleSignup = async () => {
    setError("");
    setGoogleLoading(true);

    try {
      // Open Google authentication popup
      const result = await signInWithPopup(
        auth,
        googleProvider
      );

      const user = result.user;

      // Create Firestore document if first time
      await createUserDocument({
        user,
        name: user.displayName || "",
        phone: "",
        referredBy: form.referralCode,
      });

      // Go to dashboard
      navigate("/dashboard", {
        replace: true,
      });
    } catch (firebaseError) {
      console.error(
        "Google Signup Error:",
        firebaseError
      );

      setError(
        getFirebaseErrorMessage(firebaseError)
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-76px)] bg-[#f6f8fb] px-5 py-10 sm:py-14">
      <div className="mx-auto grid max-w-6xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_25px_70px_rgba(15,23,42,0.08)] lg:grid-cols-[0.9fr_1.1fr]">

        {/* ==================================================
            LEFT SIDE
        ================================================== */}

        <div className="relative hidden overflow-hidden bg-[#07121f] p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14">

          {/* Glow */}

          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />

          <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />

          <div className="relative">

            {/* Logo */}

            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00a96d] text-lg font-black shadow-lg shadow-emerald-500/20">
                R
              </div>

              <div>
                <div className="text-lg font-black">
                  Ref
                  <span className="text-emerald-400">
                    rex
                  </span>
                </div>

                <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  WhatsApp Automation
                </div>
              </div>
            </div>

            {/* Main Text */}

            <div className="mt-16 max-w-md">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">
                Start Your Journey
              </div>

              <h1 className="mt-4 text-4xl font-black leading-tight tracking-[-0.05em] xl:text-5xl">
                Automate WhatsApp.
                <br />
                Build Your Network.
              </h1>

              <p className="mt-5 text-sm leading-7 text-slate-400">
                Create your Referx account, access the
                platform and start building your referral
                network.
              </p>
            </div>

            {/* Benefits */}

            <div className="mt-10 space-y-4">

              <Benefit
                number="01"
                text="Create your secure Referx account"
              />

              <Benefit
                number="02"
                text="Access WhatsApp automation software"
              />

              <Benefit
                number="03"
                text="Complete tasks and build your network"
              />

            </div>
          </div>

          {/* Security */}

          <div className="relative">
            <div className="flex items-center gap-3 text-xs font-bold text-slate-400">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 text-emerald-400">
                <LockKeyhole size={15} />
              </div>

              Secure account setup
            </div>
          </div>
        </div>

        {/* ==================================================
            RIGHT SIDE / FORM
        ================================================== */}

        <div className="p-6 sm:p-10 lg:p-12 xl:p-14">
          <div className="mx-auto max-w-xl">

            {/* Mobile Logo */}

            <div className="lg:hidden">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[#00a96d] text-lg font-black text-white shadow-lg shadow-emerald-500/20">
                R
              </div>
            </div>

            {/* Header */}

            <div className="mt-4 text-center lg:mt-0 lg:text-left">

              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
                Create Account
              </div>

              <h2 className="mt-2 text-3xl font-black tracking-[-0.05em] text-[#08111f]">
                Join Referx
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-500">
                Create your account to access the Referx
                platform.
              </p>

            </div>

            {/* ==================================================
                REFERRAL DETECTED
            ================================================== */}

            {referralFromUrl && (
              <div className="mt-6 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">

                <div className="flex items-center gap-2 text-xs font-black text-emerald-700">
                  <Gift size={15} />

                  Referral code detected
                </div>

                <div className="mt-1 text-xs font-bold text-emerald-600">
                  {referralFromUrl}
                </div>

              </div>
            )}

            {/* ==================================================
                ERROR
            ================================================== */}

            {error && (
              <div className="mt-5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs font-bold leading-5 text-rose-600">
                {error}
              </div>
            )}

            {/* ==================================================
                SIGNUP FORM
            ================================================== */}

            <form
              onSubmit={handleSubmit}
              className="mt-7 space-y-4"
            >

              {/* Name */}

              <InputField
                label="Full Name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                icon={UserRound}
                disabled={
                  loading || googleLoading
                }
              />

              {/* Email */}

              <InputField
                label="Email Address"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                icon={Mail}
                disabled={
                  loading || googleLoading
                }
              />

              {/* Phone */}

              <InputField
                label="Phone Number"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="10-digit mobile number"
                icon={Phone}
                maxLength={10}
                disabled={
                  loading || googleLoading
                }
              />

              {/* Password */}

              <PasswordField
                label="Password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Create a password"
                show={showPassword}
                onToggle={() =>
                  setShowPassword(
                    (previous) => !previous
                  )
                }
                disabled={
                  loading || googleLoading
                }
              />

              {/* Confirm Password */}

              <PasswordField
                label="Re-enter Password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your password"
                show={showConfirmPassword}
                onToggle={() =>
                  setShowConfirmPassword(
                    (previous) => !previous
                  )
                }
                disabled={
                  loading || googleLoading
                }
              />

              {/* Referral Code */}

              <InputField
                label="Referral Code (Optional)"
                name="referralCode"
                type="text"
                value={form.referralCode}
                onChange={handleChange}
                placeholder="Enter referral code"
                icon={Gift}
                disabled={
                  loading ||
                  googleLoading ||
                  Boolean(referralFromUrl)
                }
                required={false}
              />

              {/* Create Account Button */}

              <button
                type="submit"
                disabled={
                  loading || googleLoading
                }
                className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#00a96d] text-sm font-black text-white shadow-lg shadow-emerald-500/15 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#00945f] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2
                      size={17}
                      className="animate-spin"
                    />

                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </button>

            </form>

            {/* ==================================================
                OR
            ================================================== */}

            <div className="my-6 flex items-center gap-3">

              <div className="h-px flex-1 bg-slate-100" />

              <span className="text-[10px] font-bold text-slate-400">
                OR
              </span>

              <div className="h-px flex-1 bg-slate-100" />

            </div>

            {/* ==================================================
                GOOGLE
            ================================================== */}

            <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={
                loading || googleLoading
              }
              className="flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white text-sm font-black text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >

              {googleLoading ? (
                <>
                  <Loader2
                    size={17}
                    className="animate-spin"
                  />

                  Connecting to Google...
                </>
              ) : (
                <>
                  {/* Google G Icon */}
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-xs font-black">
                    G
                  </span>

                  Continue with Google
                </>
              )}

            </button>

            {/* ==================================================
                LOGIN
            ================================================== */}

            <div className="my-7 flex items-center gap-3">

              <div className="h-px flex-1 bg-slate-100" />

              <span className="text-[10px] font-bold text-slate-400">
                ALREADY REGISTERED?
              </span>

              <div className="h-px flex-1 bg-slate-100" />

            </div>

            <Link
              to="/login"
              className="flex h-12 w-full items-center justify-center rounded-xl border border-slate-200 bg-white text-sm font-black text-slate-700 transition-all hover:border-emerald-200 hover:bg-emerald-50 hover:text-[#00a96d]"
            >
              Login to Your Account
            </Link>

            {/* Terms */}

            <p className="mt-6 text-center text-[10px] leading-5 text-slate-400">
              By creating an account, you agree to
              Referx's Terms & Conditions and Privacy
              Policy.
            </p>

          </div>
        </div>
      </div>
    </main>
  );
}

/* =========================================================
   INPUT FIELD
========================================================= */

function InputField({
  label,
  name,
  type,
  value,
  onChange,
  placeholder,
  icon: Icon,
  maxLength,
  disabled,
  required = true,
}) {
  return (
    <div>

      <label className="mb-2 block text-xs font-black text-slate-700">
        {label}
      </label>

      <div className="relative">

        <Icon
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
        />

        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          required={required}
          className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/5 disabled:cursor-not-allowed disabled:opacity-60"
        />

      </div>
    </div>
  );
}

/* =========================================================
   PASSWORD FIELD
========================================================= */

function PasswordField({
  label,
  name,
  value,
  onChange,
  placeholder,
  show,
  onToggle,
  disabled,
}) {
  return (
    <div>

      <label className="mb-2 block text-xs font-black text-slate-700">
        {label}
      </label>

      <div className="relative">

        <LockKeyhole
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
        />

        <input
          required
          type={show ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="new-password"
          className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-11 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/5 disabled:cursor-not-allowed disabled:opacity-60"
        />

        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-700 disabled:cursor-not-allowed"
        >
          {show ? (
            <EyeOff size={17} />
          ) : (
            <Eye size={17} />
          )}
        </button>

      </div>
    </div>
  );
}

/* =========================================================
   BENEFIT
========================================================= */

function Benefit({ number, text }) {
  return (
    <div className="flex items-center gap-3">

      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 text-[9px] font-black text-emerald-400">
        {number}
      </div>

      <span className="text-xs font-bold text-slate-400">
        {text}
      </span>

    </div>
  );
}

export default Signup;